import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [0, "All time"]];

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5">
      <p className="text-xs uppercase tracking-wider text-[#5C5A56]">{label}</p>
      <p data-testid={testid} className="text-3xl font-semibold text-[#2C2B29] mt-1">{value}</p>
      {sub && <p className="text-xs text-[#5C5A56] mt-1">{sub}</p>}
    </div>
  );
}

function BarList({ title, rows, testid, empty = "No clicks in this period." }) {
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div data-testid={testid} className="min-w-0 bg-white border border-[#E5E0D8] rounded-2xl p-6">
      <h3 className="font-display text-xl text-[#2C2B29] mb-4">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-[#5C5A56]">{empty}</p> : (
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <li key={r.label} className="text-sm">
              <div className="flex justify-between gap-3"><span className="truncate text-[#2C2B29]">{r.label}</span><b className="text-[#D17A58]">{r.clicks}</b></div>
              <div className="h-1.5 rounded-full bg-[#EADDC8]/50 mt-1"><div className="h-full rounded-full bg-[#5B7B53]" style={{ width: `${(r.clicks / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [alerts, setAlerts] = useState(null);
  useEffect(() => { api.get("/price-alerts/stats").then((r) => setAlerts(r.data)); }, []);
  useEffect(() => { setD(null); api.get("/clicks/analytics", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  const change = d?.change_pct;
  const mobile = d?.devices.find((x) => x.device === "mobile")?.clicks || 0;
  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display text-4xl text-[#2C2B29]">Click Analytics</h1>
        <div className="flex gap-2">
          {RANGES.map(([v, label]) => (
            <button key={v} data-testid={`analytics-range-${v}`} onClick={() => setDays(v)} className={`px-4 py-2 rounded-full text-sm border ${days === v ? "bg-[#2C2B29] text-white border-[#2C2B29]" : "bg-white border-[#E5E0D8]"}`}>{label}</button>
          ))}
        </div>
      </div>
      {!d ? <p className="text-[#5C5A56]">Loading…</p> : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Stat label='"Buy Now" clicks' value={d.total} testid="analytics-total"
              sub={change === null || change === undefined ? (days ? "No clicks in the previous period" : "Since tracking began") : `${change >= 0 ? "▲" : "▼"} ${Math.abs(change)}% vs previous ${days} days`} />
            <Stat label="Avg per day" value={(d.total / Math.max(1, d.daily.length)).toFixed(1)} testid="analytics-avg" />
            <Stat label="From phones" value={d.total ? `${Math.round((mobile / d.total) * 100)}%` : "—"} testid="analytics-mobile-share" />
          </div>
          <div data-testid="analytics-chart" className="bg-white border border-[#E5E0D8] rounded-2xl p-6 mb-6 h-72">
            <ResponsiveContainer>
              <AreaChart data={d.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} stroke="#5C5A56" minTickGap={20} />
                <YAxis allowDecimals={false} fontSize={11} stroke="#5C5A56" width={30} />
                <Tooltip />
                <Area type="monotone" dataKey="clicks" stroke="#D17A58" fill="#D17A58" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <BarList title="Top products" testid="analytics-top-products" rows={d.top_products.map((r) => ({ label: r.title, clicks: r.clicks }))} />
            <BarList title="Top pages & articles" testid="analytics-top-pages" rows={d.top_pages.map((r) => ({ label: r.label, clicks: r.clicks }))} />
            <BarList title="Devices" testid="analytics-devices" rows={d.devices.map((r) => ({ label: r.device, clicks: r.clicks }))} />
            <BarList title="Where visitors came from" testid="analytics-referrers" rows={d.referrers.map((r) => ({ label: r.referrer, clicks: r.clicks }))} />
            {alerts && (
              <BarList
                title={`Price drop alerts · ${alerts.active} active · ${alerts.sent} emails sent`}
                testid="analytics-price-alerts"
                empty="No readers are watching a price yet."
                rows={alerts.top_products.map((r) => ({ label: r.title, clicks: r.watchers }))}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
